/*
 * Copyright (c) Nexign, JSC, 2019-2024.
 */
/**
 * Initial translations
 *
 * @author Yakov Gushchin
 * @author Evgenij Tyan
 * @author Anton Panasenko
 * @author Maxim Fedorov
 * @author Aleksandr Perkhunov
 * @author Julia Ignatieva
 * @author Dmitriy Kvon
 * @author Ilya Babanov
 * @author Evgeniy Naumov
 * @author Nikolay Sviridov
 * @version 1.32.0
 * @since 1.0.0
 */

import { initWizardAdditionalReportsTranslation } from '@modules/internationalization/constants/wizards/initWizardAdditionalReportsTranslation';
import {
    initWizardChangeRoleTranslation,
} from '@modules/internationalization/constants/wizards/initWizardChangeRoleTranslation';

import { ITranslation } from '../types';
import initCockpitTranslation from './cockpit';
import initTranslationCommon from './common';
import { initTranslationFastActions } from './components/fastActions/fastActions';
import initTranslationCorporate from './corporate';
import { initialCreateInquiryTranslation } from './modules/createInquiry';
import { initialEdmOperatorConnectionTranslation } from './modules/edmOperatorConnection';
import { initialFormAddress } from './modules/formAddress';
import { initialOnboardingTranslation } from './modules/onboarding';
import { initialPersonalDataConfirmTranslation } from './modules/personalDataConfirm';
import { initialPersonalDataDeleteTranslation } from './modules/personalDataDelete';
import initPromisedPaymentTranslation from './modules/promisedPayments';
import { initialUserNotificationsTranslation } from './modules/userNotifications';
import initTranslationPageAuth from './pages/auth';
import initTranslationPageAuthServiceAccess from './pages/authServiceAccess';
import { initPageAutoReportIdTranslation } from './pages/autoReportId';
import initTranslationPageBills from './pages/bills';
import { initPageCampaignInfoTranslation } from './pages/campaignInfo';
import initPageCampaignsTranslation from './pages/campaigns';
import { initPageCashbackTranslation } from './pages/cashback';
import initTranslationPageDashboard from './pages/dashboard/dashboard';
import { initTranslationPageExpensesAnalyzer } from './pages/expensesAnalyzer';
import initTranslationPageHistoryInquiry from './pages/historyInquiry';
import initTranslationPageHistoryOrder from './pages/historyOrder';
import { initPageInquiryInfoTranslation } from './pages/inquiryInfo';
import { initPageNewsTranslation } from './pages/news';
import { initPageOrderIdTranslation } from './pages/orderId';
import { initPageAccountInfoTranslation } from './pages/pageAccountInfoTranslation';
import { initTranslationPageSettings } from './pages/pageSettings';
import { initPagePaymentsTranslation } from './pages/payments';
import initTranslationPagePersonalAccount from './pages/personalAccount';
import { initTranslationPageProducts } from './pages/products';
import { initPageReportsConstructorTranslation } from './pages/reportsConstructor';
import initTranslationPageSubscriberFixed from './pages/subscriberFixed';
import initTranslationPageSubscriberId from './pages/subscriberId';
import initTranslationPageSubscriberMobile from './pages/subscriberMobile';
import { initCallsDetailTranslation } from './wizards/initCallsDetailTranslation';
import { initLabelsImportTranslation } from './wizards/initLabelsImportTranslation';
import { initMoneyTransferTranslation } from './wizards/initMoneyTransferTranslation';
import { initWizardAdditionalAccountsTranslation } from './wizards/initWizardAdditionalAccountsTranslation';
import { initWizardBillsDocTranslation } from './wizards/initWizardBillsDocTranslation';
import { initWizardChangeLockTranslation } from './wizards/initWizardChangeLockTranslation';
import { initWizardCollationReportTranslation } from './wizards/initWizardCollationReportTranslation';
import { initWizardExpensesDetailTranslation } from './wizards/initWizardExpensesDetailTranslation';
import { initWizardImportPersonalDataTranslation } from './wizards/initWizardImportPersonalDataTranslation';
import { initWizardImportSubscribersLimitsTranslation } from './wizards/initWizardImportSubscribersLimitsTranslation';
import { initWizardMultiSearchTranslation } from './wizards/initWizardMultiSearch';
import { initWizardOrderReportTranslation } from './wizards/initWizardOrderReport';
import { initWizardOrderTemplateTranslation } from './wizards/initWizardOrderTemplate';
import { initWizardProductOfferingsTranslation } from './wizards/initWizardProductOfferingsTranslation';
import { initWizardTranslation } from './wizards/initWizardTranslation';

export const SET_LOCALE = '@@b2b_portal_ui/internationalization/SET_LOCALE';

export const LOAD_INTERNAL_TRANSLATIONS_START = '@@b2b_portal_ui/internationalization/LOAD_INTERNAL_TRANSLATIONS_START';
export const LOAD_INTERNAL_TRANSLATIONS_SUCCESS =
    '@@b2b_portal_ui/internationalization/LOAD_INTERNAL_TRANSLATIONS_SUCCESS';
export const LOAD_INTERNAL_TRANSLATIONS_FAIL = '@@b2b_portal_ui/internationalization/LOAD_INTERNAL_TRANSLATIONS_FAIL';

export const LOAD_EXTERNAL_TRANSLATIONS_START = '@@b2b_portal_ui/internationalization/LOAD_EXTERNAL_TRANSLATIONS_START';
export const LOAD_EXTERNAL_TRANSLATIONS_SUCCESS =
    '@@b2b_portal_ui/internationalization/LOAD_EXTERNAL_TRANSLATIONS_SUCCESS';
export const LOAD_EXTERNAL_TRANSLATIONS_FAILURE =
    '@@b2b_portal_ui/internationalization/LOAD_EXTERNAL_TRANSLATIONS_FAILURE';
export const LOAD_EXTERNAL_TRANSLATIONS_FINISH =
    '@@b2b_portal_ui/internationalization/LOAD_EXTERNAL_TRANSLATIONS_FINISH';

export enum TranslationFileNamesEnum {
    callsDetail = 'callsDetail',
    cockpit = 'cockpit',
    common = 'common',
    corporate = 'corporate',
    createInquiry = 'createInquiry',
    edmOperatorConnection = 'edmOperatorConnection',
    fastActions = 'fastActions',
    formAddress = 'formAddress',
    labelsImport = 'labelsImport',
    moneyTransfer = 'moneyTransfer',
    onboarding = 'onboarding',
    pageAccountInfo = 'pageAccountInfo',
    pageAuth = 'pageAuth',
    pageAuthServiceAccess = 'pageAuthServiceAccess',
    pageAutoReportId = 'pageAutoReportId',
    pageBills = 'pageBills',
    pageCampaignInfo = 'pageCampaignInfo',
    pageCampaigns = 'pageCampaigns',
    pageCashback = 'pageCashback',
    pageDashboard = 'pageDashboard',
    pageExpensesAnalyzer = 'pageExpensesAnalyzer',
    pageHistoryInquiry = 'pageHistoryInquiry',
    pageHistoryOrder = 'pageHistoryOrder',
    pageInquiryInfo = 'pageInquiryInfo',
    pageNews = 'pageNews',
    pageOrderId = 'pageOrderId',
    pagePayments = 'pagePayments',
    pagePersonalAccount = 'pagePersonalAccount',
    pageProducts = 'pageProducts',
    pageReportsConstructor = 'pageReportsConstructor',
    pageSettings = 'pageSettings',
    pageSubscriberFixed = 'pageSubscriberFixed',
    pageSubscriberId = 'pageSubscriberId',
    pageSubscriberMobile = 'pageSubscriberMobile',
    personalDataConfirm = 'personalDataConfirm',
    personalDataDelete = 'personalDataDelete',
    promisedPayment = 'promisedPayment',
    userNotifications = 'userNotifications',
    wizard = 'wizard',
    wizardAdditionalAccounts = 'wizardAdditionalAccounts',
    wizardAdditionalReports = 'wizardAdditionalReports',
    wizardBillsDoc = 'wizardBillsDoc',
    wizardChangeLock = 'wizardChangeLock',
    wizardExpensesDetail = 'wizardExpensesDetail',
    wizardImportPersonalData = 'wizardImportPersonalData',
    wizardProductOfferings = 'wizardProductOfferings',
    wizardMultiSearch = 'wizardMultiSearch',
    wizardOrderTemplate = 'wizardOrderTemplate',
    wizardOrderReport = 'wizardOrderReport',
    wizardCollationReport = 'wizardCollationReport',
    wizardImportSubscribersLimits = 'wizardImportSubscribersLimits',
    wizardChangeRole = 'wizardChangeRole',
}

export type TranslationFileNamesType = keyof typeof TranslationFileNamesEnum;

export const initTranslation: ITranslation = {
    callsDetail: initCallsDetailTranslation,
    cockpit: initCockpitTranslation,
    common: initTranslationCommon,
    corporate: initTranslationCorporate,
    createInquiry: initialCreateInquiryTranslation,
    edmOperatorConnection: initialEdmOperatorConnectionTranslation,
    fastActions: initTranslationFastActions,
    formAddress: initialFormAddress,
    labelsImport: initLabelsImportTranslation,
    moneyTransfer: initMoneyTransferTranslation,
    onboarding: initialOnboardingTranslation,
    pageAccountInfo: initPageAccountInfoTranslation,
    pageAuth: initTranslationPageAuth,
    pageAuthServiceAccess: initTranslationPageAuthServiceAccess,
    pageAutoReportId: initPageAutoReportIdTranslation,
    pageBills: initTranslationPageBills,
    pageCampaignInfo: initPageCampaignInfoTranslation,
    pageCampaigns: initPageCampaignsTranslation,
    pageCashback: initPageCashbackTranslation,
    pageDashboard: initTranslationPageDashboard,
    pageExpensesAnalyzer: initTranslationPageExpensesAnalyzer,
    pageHistoryInquiry: initTranslationPageHistoryInquiry,
    pageHistoryOrder: initTranslationPageHistoryOrder,
    pageInquiryInfo: initPageInquiryInfoTranslation,
    pageNews: initPageNewsTranslation,
    pageOrderId: initPageOrderIdTranslation,
    pagePayments: initPagePaymentsTranslation,
    pagePersonalAccount: initTranslationPagePersonalAccount,
    pageProducts: initTranslationPageProducts,
    pageReportsConstructor: initPageReportsConstructorTranslation,
    pageSettings: initTranslationPageSettings,
    pageSubscriberFixed: initTranslationPageSubscriberFixed,
    pageSubscriberId: initTranslationPageSubscriberId,
    pageSubscriberMobile: initTranslationPageSubscriberMobile,
    personalDataConfirm: initialPersonalDataConfirmTranslation,
    personalDataDelete: initialPersonalDataDeleteTranslation,
    promisedPayment: initPromisedPaymentTranslation,
    userNotifications: initialUserNotificationsTranslation,
    wizard: initWizardTranslation,
    wizardAdditionalAccounts: initWizardAdditionalAccountsTranslation,
    wizardAdditionalReports: initWizardAdditionalReportsTranslation,
    wizardBillsDoc: initWizardBillsDocTranslation,
    wizardChangeLock: initWizardChangeLockTranslation,
    wizardExpensesDetail: initWizardExpensesDetailTranslation,
    wizardImportPersonalData: initWizardImportPersonalDataTranslation,
    wizardMultiSearch: initWizardMultiSearchTranslation,
    wizardOrderTemplate: initWizardOrderTemplateTranslation,
    wizardOrderReport: initWizardOrderReportTranslation,
    wizardProductOfferings: initWizardProductOfferingsTranslation,
    wizardCollationReport: initWizardCollationReportTranslation,
    wizardImportSubscribersLimits: initWizardImportSubscribersLimitsTranslation,
    wizardChangeRole: initWizardChangeRoleTranslation,
};
